// Float math for the renderer — vec3, mat4, projections
// Separate from fixed.ts (sim = integers, renderer = floats)

export type Vec3 = [number, number, number];
export type Mat4 = Float32Array; // 16 elements, column-major

// --- Vec3 ---

export function v3Sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

export function v3Cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

export function v3Dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function v3Normalize(v: Vec3): Vec3 {
  const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  if (len === 0) return [0, 0, 0];
  return [v[0] / len, v[1] / len, v[2] / len];
}

// --- Mat4 ---

export function mat4Identity(): Mat4 {
  const m = new Float32Array(16);
  m[0] = 1; m[5] = 1; m[10] = 1; m[15] = 1;
  return m;
}

export function mat4Multiply(a: Mat4, b: Mat4): Mat4 {
  const m = new Float32Array(16);
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      let sum = 0;
      for (let k = 0; k < 4; k++) {
        sum += a[row + k * 4] * b[k + col * 4];
      }
      m[row + col * 4] = sum;
    }
  }
  return m;
}

export function mat4Ortho(
  left: number, right: number,
  bottom: number, top: number,
  near: number, far: number
): Mat4 {
  const m = new Float32Array(16);
  m[0] = 2 / (right - left);
  m[5] = 2 / (top - bottom);
  // WebGPU clip space: Z ∈ [0, 1]
  m[10] = -1 / (far - near);
  m[12] = -(right + left) / (right - left);
  m[13] = -(top + bottom) / (top - bottom);
  m[14] = -near / (far - near);
  m[15] = 1;
  return m;
}

export function mat4Perspective(
  fovY: number, aspect: number,
  near: number, far: number
): Mat4 {
  const m = new Float32Array(16);
  const f = 1 / Math.tan(fovY / 2);
  m[0] = f / aspect;
  m[5] = f;
  // WebGPU clip space: Z ∈ [0, 1] (not [-1, 1] like OpenGL)
  m[10] = far / (near - far);
  m[11] = -1;
  m[14] = (far * near) / (near - far);
  return m;
}

export function mat4Invert(a: Mat4): Mat4 {
  const m = new Float32Array(16);
  const a00=a[0],a01=a[1],a02=a[2],a03=a[3];
  const a10=a[4],a11=a[5],a12=a[6],a13=a[7];
  const a20=a[8],a21=a[9],a22=a[10],a23=a[11];
  const a30=a[12],a31=a[13],a32=a[14],a33=a[15];

  const b00=a00*a11-a01*a10, b01=a00*a12-a02*a10;
  const b02=a00*a13-a03*a10, b03=a01*a12-a02*a11;
  const b04=a01*a13-a03*a11, b05=a02*a13-a03*a12;
  const b06=a20*a31-a21*a30, b07=a20*a32-a22*a30;
  const b08=a20*a33-a23*a30, b09=a21*a32-a22*a31;
  const b10=a21*a33-a23*a31, b11=a22*a33-a23*a32;

  let det=b00*b11-b01*b10+b02*b09+b03*b08-b04*b07+b05*b06;
  if (!det) return mat4Identity();
  det = 1.0 / det;

  m[0]=(a11*b11-a12*b10+a13*b09)*det;
  m[1]=(a02*b10-a01*b11-a03*b09)*det;
  m[2]=(a31*b05-a32*b04+a33*b03)*det;
  m[3]=(a22*b04-a21*b05-a23*b03)*det;
  m[4]=(a12*b08-a10*b11-a13*b07)*det;
  m[5]=(a00*b11-a02*b08+a03*b07)*det;
  m[6]=(a32*b02-a30*b05-a33*b01)*det;
  m[7]=(a20*b05-a22*b02+a23*b01)*det;
  m[8]=(a10*b10-a11*b08+a13*b06)*det;
  m[9]=(a01*b08-a00*b10-a03*b06)*det;
  m[10]=(a30*b04-a31*b02+a33*b00)*det;
  m[11]=(a21*b02-a20*b04-a23*b00)*det;
  m[12]=(a11*b07-a10*b09-a12*b06)*det;
  m[13]=(a00*b09-a01*b07+a02*b06)*det;
  m[14]=(a31*b01-a30*b03-a32*b00)*det;
  m[15]=(a20*b03-a21*b01+a22*b00)*det;
  return m;
}

export function mat4LookAt(eye: Vec3, target: Vec3, up: Vec3): Mat4 {
  const f = v3Normalize(v3Sub(target, eye));
  const s = v3Normalize(v3Cross(f, up));
  const u = v3Cross(s, f);

  const m = new Float32Array(16);
  m[0] = s[0];  m[4] = s[1];  m[8]  = s[2];
  m[1] = u[0];  m[5] = u[1];  m[9]  = u[2];
  m[2] = -f[0]; m[6] = -f[1]; m[10] = -f[2];
  m[12] = -v3Dot(s, eye);
  m[13] = -v3Dot(u, eye);
  m[14] = v3Dot(f, eye);
  m[15] = 1;
  return m;
}
